<hawk-record-navigation
  [breadcrumbLinkList]="[
    { description: 'Lager', link: '/a/lager/uebersicht' },
    { description: 'Erwartete Lieferungen', link: '/a/lager/erwartete-lieferungen' }
  ]"
  [root]="'warehouse'"
  [leafDescription]="'Neue Lieferung erfassen'"
  class="app-page"
>
  <div class="page-content page-frame overflow-y-auto">
    <div class="page-sheet max-w-4xl mx-auto">
      <!-- Fehlermeldung -->
      @if (errorMessage) {
        <div class="alert alert-error mb-4">
          <i class="fa fa-exclamation-triangle"></i>
          <span>{{ errorMessage }}</span>
        </div>
      }

      <form [formGroup]="deliveryForm" (ngSubmit)="onSubmit()">

        <!-- STEP 1: Kopfdaten -->
        @if (currentStep === 1) {
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Bestelldaten -->
            <section class="bm-card">
              <fieldset class="bm-card-body bm-fieldset">
                <h2 class="bm-card-title">Bestellung</h2>

                <label class="bm-fieldset-label">Bestelldatum</label>
                <div class="bm-join w-full">
                  <input
                    class="bm-input bm-join-item w-full"
                    [matDatepicker]="orderDatePicker"
                    formControlName="order_date"
                  />
                  <mat-datepicker #orderDatePicker />
                  <button
                    type="button"
                    class="bm-btn bm-join-item"
                    (click)="orderDatePicker.open()"
                  >
                    <i class="fa fa-calendar"></i>
                  </button>
                </div>

                <label class="bm-fieldset-label">Erwartete Lieferform</label>
                <select class="bm-select w-full" formControlName="packaging_type">
                  <option value="">-- Nicht angegeben --</option>
                  <option value="PALLET">Palette</option>
                  <option value="PACKAGE">Paket</option>
                </select>

                <label class="bm-fieldset-label">Handlungsanweisung *</label>
                @if (!showCustomHandlingInstruction) {
                  <select
                    class="bm-select w-full"
                    [class.bm-select-error]="deliveryForm.get('handling_instruction')?.invalid && deliveryForm.get('handling_instruction')?.touched"
                    formControlName="handling_instruction"
                    (change)="onHandlingInstructionChange($event)"
                  >
                    <option value="">-- Bitte auswählen --</option>
                    @for (option of handlingInstructionOptions; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                } @else {
                  <div class="flex gap-2">
                    <input
                      class="bm-input flex-1"
                      type="text"
                      [(ngModel)]="customHandlingInstruction"
                      [ngModelOptions]="{standalone: true}"
                      (ngModelChange)="onCustomHandlingInstructionChange()"
                      placeholder="Freitext-Handlungsanweisung eingeben..."
                    />
                    <button
                      type="button"
                      class="bm-btn bm-btn-ghost"
                      (click)="showCustomHandlingInstruction = false; deliveryForm.get('handling_instruction')?.setValue('')"
                      title="Zurück zur Auswahl"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                }

                <label class="bm-fieldset-label">Hinweise für Lageristen</label>
                <textarea
                  class="bm-textarea w-full"
                  formControlName="notes"
                  rows="3"
                  placeholder="Besondere Hinweise zur Lieferung..."
                ></textarea>
              </fieldset>
            </section>

            <!-- Projekt & Kunde -->
            <section class="bm-card">
              <fieldset class="bm-card-body bm-fieldset">
                <h2 class="bm-card-title">Zuordnung</h2>

                <label class="bm-fieldset-label" for="ticketNumber">Ticket (optional)</label>
                <div class="relative">
                  <input
                    id="ticketNumber"
                    name="ticketNumber"
                    class="bm-input w-full pr-10"
                    type="text"
                    [(ngModel)]="ticketNumber"
                    [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onTicketNumberChange()"
                    (blur)="checkTicket()"
                    (keydown.enter)="checkTicket(); $event.preventDefault()"
                    placeholder="z.B. MU-25-209720"
                  />
                  <!-- Status-Icon -->
                  <div class="absolute right-3 top-1/2 -translate-y-1/2">
                    <span
                      class="loading loading-spinner loading-sm text-primary"
                      [class.hidden]="ticketCheckStatus !== 'checking'"
                    ></span>
                    @if (ticketNumber) {
                      <button
                        type="button"
                        class="text-success hover:text-error transition-colors"
                        [class.hidden]="ticketCheckStatus !== 'found'"
                        (click)="clearTicket()"
                        title="Ticket-Zuordnung entfernen"
                      >
                        x
                      </button>
                    }
                  </div>
                </div>
                @if (ticketCheckStatus === 'not_found') {
                  <p class="text-sm text-error mt-1">
                    <i class="fa fa-exclamation-triangle mr-1"></i>
                    Ticket nicht gefunden.
                  </p>
                }

                <label class="bm-fieldset-label">Projekt</label>
                <select class="bm-select w-full" formControlName="project_id" (change)="onProjectChange()">
                  <option [ngValue]="null">-- Kein Projekt --</option>
                  @for (project of projects; track project.id) {
                    <option [ngValue]="project.id">{{ project.name }}</option>
                  }
                </select>

                <label class="bm-fieldset-label">Kunde</label>
                <select class="bm-select w-full" formControlName="customer_id" (change)="onCustomerChange()">
                  <option [ngValue]="null">-- Kein Kunde (Eigenware) --</option>
                  @for (customer of customers; track customer.id) {
                    <option [ngValue]="customer.id">{{ customer.name }}</option>
                  }
                </select>

                @if (!deliveryForm.get('customer_id')?.value) {
                  <label class="bm-fieldset-label">Lieferant</label>
                  <input
                    class="bm-input w-full"
                    type="text"
                    formControlName="supplier_name"
                    placeholder="z.B. Bechtle"
                  />
                }
              </fieldset>
            </section>

            <!-- Lieferzeitraum -->
            <section class="bm-card">
              <fieldset class="bm-card-body bm-fieldset">
                <h2 class="bm-card-title">Erwarteter Lieferzeitraum</h2>

                <label class="bm-fieldset-label">Erwartet ab</label>
                <div class="bm-join w-full">
                  <input
                    class="bm-input bm-join-item w-full"
                    [matDatepicker]="expectedFromPicker"
                    formControlName="expected_from"
                  />
                  <mat-datepicker #expectedFromPicker />
                  <button
                    type="button"
                    class="bm-btn bm-join-item"
                    (click)="expectedFromPicker.open()"
                  >
                    <i class="fa fa-calendar"></i>
                  </button>
                </div>

                <label class="bm-fieldset-label">Erwartet bis</label>
                <div class="bm-join w-full">
                  <input
                    class="bm-input bm-join-item w-full"
                    [matDatepicker]="expectedUntilPicker"
                    formControlName="expected_until"
                  />
                  <mat-datepicker #expectedUntilPicker />
                  <button
                    type="button"
                    class="bm-btn bm-join-item"
                    (click)="expectedUntilPicker.open()"
                  >
                    <i class="fa fa-calendar"></i>
                  </button>
                </div>
                <p class="text-sm text-base-content/60">
                  Bei Überschreitung des "Erwartet bis"-Datums wird eine Erinnerung versendet.
                </p>
              </fieldset>
            </section>

            <!-- Benachrichtigungen -->
            <section class="bm-card">
              <fieldset class="bm-card-body bm-fieldset">
                <h2 class="bm-card-title">Benachrichtigungen</h2>

                <div class="flex items-center justify-between">
                  <label class="bm-fieldset-label mb-0">Interne E-Mails</label>
                  <button
                    type="button"
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="openAdminUserModal()"
                    title="Admin-User auswählen"
                  >
                    <i class="fa fa-users mr-1"></i>
                    Auswählen
                  </button>
                </div>
                <input
                  class="bm-input w-full"
                  type="text"
                  formControlName="internal_notification_emails"
                  placeholder="email1@firma.de; email2@firma.de"
                />

                <label class="bm-fieldset-label">Externe E-Mails</label>
                <input
                  class="bm-input w-full"
                  type="text"
                  formControlName="external_notification_emails"
                  placeholder="kunde@example.com"
                />
                <p class="text-sm text-base-content/60">
                  Mehrere E-Mails mit Semikolon trennen. Helpdesk wird bei Externen Mails automatisch mitinformiert.
                </p>
              </fieldset>
            </section>

            <!-- Interner Lieferschein (volle Breite) -->
            <section class="bm-card md:col-span-2">
              <fieldset class="bm-card-body bm-fieldset">
                <h2 class="bm-card-title">Interner Lieferschein</h2>

                <!-- Checkbox für Lieferschein-Erstellung -->
                <label class="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-base-200 mb-4">
                  <input
                    type="checkbox"
                    class="bm-checkbox"
                    [(ngModel)]="createDeliveryNote"
                    [ngModelOptions]="{standalone: true}"
                  />
                  <div>
                    <span class="font-medium">Lieferschein automatisch erstellen</span>
                    <p class="text-sm text-base-content/60 mt-0.5">
                      Empfänger ist das Bentomax Hauptlager. Absender-Adresse unten angeben oder oben Kunden auswählen.
                    </p>
                  </div>
                </label>

                @if (createDeliveryNote) {
                  <!-- Absender-Adresse -->
                  @if (getSelectedCustomer(); as customer) {
                    <!-- Kundenadresse wird automatisch übernommen -->
                    <div class="bm-alert bm-alert-info mb-4">
                      <i class="fa fa-info-circle"></i>
                      <span>Absender-Adresse wird automatisch vom Kunden übernommen.</span>
                    </div>
                    <div class="bg-base-200 rounded-lg p-4">
                      <p class="font-medium">{{ customer.company || customer.name }}</p>
                      @if (customer.street) {
                        <p class="text-sm text-base-content/70">{{ customer.street }}</p>
                      }
                      @if (customer.zip || customer.city) {
                        <p class="text-sm text-base-content/70">{{ customer.zip }} {{ customer.city }}</p>
                      }
                      @if (customer.country) {
                        <p class="text-sm text-base-content/70">{{ customer.country }}</p>
                      }
                    </div>
                  } @else {
                    <!-- Manuelle Eingabe wenn kein Kunde ausgewählt -->
                    <div class="bm-fieldset">
                      <!-- Firma (volle Breite) -->
                      <label class="bm-fieldset-label">Firma</label>
                      <input
                        class="bm-input w-full"
                        type="text"
                        formControlName="sender_address_company"
                        placeholder="z.B. Bechtle GmbH"
                      />

                      <!-- Vorname / Nachname -->
                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <label class="bm-fieldset-label">Vorname</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_firstname"
                          />
                        </div>
                        <div>
                          <label class="bm-fieldset-label">Nachname</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_lastname"
                          />
                        </div>
                      </div>

                      <!-- Straße / Nr. -->
                      <div class="flex gap-4">
                        <div class="flex-1">
                          <label class="bm-fieldset-label">Straße</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_street"
                          />
                        </div>
                        <div class="w-24">
                          <label class="bm-fieldset-label">Nr.</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_street_no"
                          />
                        </div>
                      </div>

                      <!-- PLZ / Ort -->
                      <div class="flex gap-4">
                        <div class="w-28">
                          <label class="bm-fieldset-label">PLZ</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_zip"
                          />
                        </div>
                        <div class="flex-1">
                          <label class="bm-fieldset-label">Ort</label>
                          <input
                            class="bm-input w-full"
                            type="text"
                            formControlName="sender_address_city"
                          />
                        </div>
                      </div>

                      <!-- Land -->
                      <label class="bm-fieldset-label">Land</label>
                      <ng-select
                        class="w-full"
                        [items]="countries"
                        formControlName="sender_address_country"
                        bindLabel="name"
                        bindValue="id"
                        [clearable]="false"
                        placeholder="Land auswählen"
                      >
                      </ng-select>
                    </div>
                  }
                } @else {
                  <p class="text-sm text-base-content/40">
                    <i class="fa fa-info-circle mr-1"></i>
                    Kein Lieferschein wird erstellt.
                  </p>
                }
              </fieldset>
            </section>

            <!-- Sendungsverfolgung (volle Breite) -->
            <section class="bm-card md:col-span-2">
              <fieldset class="bm-card-body bm-fieldset">
                <div class="flex justify-between items-center mb-2">
                  <h2 class="bm-card-title mb-0">Sendungsverfolgung</h2>
                  <button
                    type="button"
                    class="bm-btn bm-btn-sm bm-btn-primary"
                    (click)="addTrackingNumber()"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Tracking-Nr. hinzufügen
                  </button>
                </div>

                @if (trackingNumbers.length === 0) {
                  <p class="text-sm text-base-content/60">
                    Noch keine Tracking-Nummern hinzugefügt. Eine Bestellung kann mehrere Lieferungen/Pakete enthalten.
                    <br/>
                    <span class="text-info">Tracking-Nummern können auch später auf der Detailseite ergänzt werden.</span>
                  </p>
                }

                <div class="space-y-2" formArrayName="tracking_numbers">
                  @for (tracking of trackingNumbers.controls; track $index; let i = $index) {
                    <div class="flex items-center gap-2" [formGroupName]="i">
                      <select class="bm-select w-32" formControlName="carrier">
                        @for (carrier of carriers; track carrier) {
                          <option [value]="carrier">{{ carrier }}</option>
                        }
                      </select>
                      <input
                        class="bm-input flex-1"
                        type="text"
                        formControlName="number"
                        placeholder="Tracking-/Lieferscheinnummer"
                      />
                      <button
                        type="button"
                        class="bm-btn bm-btn-ghost bm-btn-sm text-error"
                        (click)="removeTrackingNumber(i)"
                        title="Entfernen"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  }
                </div>
              </fieldset>
            </section>
          </div>

          <!-- Step 1 Actions -->
          <div class="flex justify-between mt-6">
            <a routerLink="/lager/erwartete-lieferungen" class="bm-btn bm-btn-ghost">
              Abbrechen
            </a>
            <button
              type="button"
              class="bm-btn bm-btn-primary"
              [disabled]="!isStep1Valid"
              (click)="nextStep()"
            >
              Weiter zu Artikelpositionen
              <i class="fa fa-arrow-right ml-2"></i>
            </button>
          </div>
        }

        <!-- STEP 2: Artikelpositionen -->
        @if (currentStep === 2) {
          <!-- Zusammenfassung Kopfdaten -->
          <section class="bm-card mb-4 bg-base-200">
            <div class="bm-card-body py-3">
              <div class="flex justify-between items-start">
                <div>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-2 text-sm">
                    @if (deliveryForm.get('supplier_name')?.value) {
                      <div>
                        <span class="text-base-content/60">Lieferant:</span>
                        <span class="ml-1 font-medium">{{ deliveryForm.get('supplier_name')?.value }}</span>
                      </div>
                    }
                    @if (trackingNumbers.length > 0) {
                      <div class="col-span-2">
                        <span class="text-base-content/60">Tracking:</span>
                        <span class="ml-1 font-medium">{{ getTrackingNumbersSummary() }}</span>
                      </div>
                    }
                    <div>
                      <span class="text-base-content/60">Projekt:</span>
                      <span class="ml-1 font-medium">{{ getProjectName() }}</span>
                    </div>
                    <div>
                      <span class="text-base-content/60">Kunde:</span>
                      <span class="ml-1 font-medium">{{ getCustomerName() }}</span>
                    </div>
                    <div>
                      <span class="text-base-content/60">Erwartete Lieferform:</span>
                      <span class="ml-1 font-medium">{{ getPackagingLabel() }}</span>
                    </div>
                    <div class="col-span-2">
                      <span class="text-base-content/60">Handlungsanweisung:</span>
                      <span class="ml-1 font-medium">{{ getHandlingInstructionLabel() }}</span>
                    </div>
                    @if (deliveryForm.get('expected_until')?.value) {
                      <div>
                        <span class="text-base-content/60">Erwartet bis:</span>
                        <span class="ml-1 font-medium">{{ deliveryForm.get('expected_until')?.value | date:'dd.MM.yyyy' }}</span>
                      </div>
                    }
                  </div>
                </div>
                <button
                  type="button"
                  class="bm-btn bm-btn-ghost bm-btn-sm"
                  (click)="previousStep()"
                >
                  <i class="fa fa-pencil"></i>
                  Bearbeiten
                </button>
              </div>
            </div>
          </section>

          <!-- Artikel-Positionen -->
          <section class="bm-card">
            <div class="bm-card-body">
              <div class="flex justify-between items-center mb-4">
                <h2 class="bm-card-title mb-0">Artikelpositionen</h2>
                <button type="button" class="bm-btn bm-btn-primary bm-btn-sm" (click)="addItem()">
                  <i class="fa fa-plus"></i>
                  Position hinzufügen
                </button>
              </div>

              @if (items.length === 0) {
                <div class="alert alert-info">
                  <i class="fa fa-info-circle"></i>
                  <span>Noch keine Positionen hinzugefügt. Klicken Sie auf "Position hinzufügen".</span>
                </div>
              }

              <div class="space-y-3">
                @for (item of items.controls; track $index; let i = $index) {
                  <div class="flex flex-wrap items-end gap-3 p-4 bg-base-200 rounded-lg">
                    <div class="flex-1 min-w-[250px]">
                      <label class="bm-fieldset-label text-sm">Artikelvorlage *</label>
                      <button
                        type="button"
                        class="bm-btn w-full justify-start text-left"
                        [class.bm-btn-ghost]="!$any(item).controls.article_template_id.value"
                        [class.border-base-300]="!$any(item).controls.article_template_id.value"
                        (click)="openTemplateModal(i)"
                      >
                        @if ($any(item).controls.article_template_id.value) {
                          <span class="truncate">{{ getTemplateName($any(item).controls.article_template_id.value) }}</span>
                        } @else {
                          <span class="text-base-content/60">
                            <i class="fa fa-search mr-2"></i>
                            Artikelvorlage auswählen...
                          </span>
                        }
                      </button>
                    </div>

                    <div class="w-28">
                      <label class="bm-fieldset-label text-sm">Menge *</label>
                      <input
                        type="number"
                        class="bm-input w-full"
                        [formControl]="$any(item).controls.quantity_ordered"
                        min="1"
                      />
                    </div>

                    <div class="w-36">
                      <label class="bm-fieldset-label text-sm">Eigentümer *</label>
                      <select class="bm-select w-full" [formControl]="$any(item).controls.owner">
                        <option value="BENTOMAX">Bentomax</option>
                        <option value="CUSTOMER">Kunde</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      class="bm-btn bm-btn-error bm-btn-sm"
                      (click)="removeItem(i)"
                      title="Position entfernen"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                }
              </div>
            </div>
          </section>

          <!-- Step 2 Actions -->
          <div class="flex justify-between mt-6">
            <button
              type="button"
              class="bm-btn bm-btn-ghost"
              (click)="previousStep()"
            >
              <i class="fa fa-arrow-left mr-2"></i>
              Zurück
            </button>
            <button
              type="submit"
              class="bm-btn bm-btn-primary"
              [disabled]="items.length === 0 || isSubmitting"
            >
              @if (isSubmitting) {
                <span class="loading loading-spinner loading-sm"></span>
              }
              Lieferung erfassen
            </button>
          </div>
        }

      </form>
    </div>
  </div>

  <!-- Artikelvorlage Auswahl Modal -->
  @if (showTemplateModal) {
    <app-article-template-picker
      [project]="getPickerProject()"
      [customer]="getPickerCustomer()"
      [initialSearchQuery]="''"
      [externalTemplates]="articleTemplates"
      (templateSelected)="selectTemplate($event)"
      (templatesReloaded)="onPickerTemplatesReloaded($event)"
      (closed)="closeTemplateModal()"
    />
  }

  <!-- Admin-User Auswahl Modal -->
  @if (showAdminUserModal) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box max-w-lg">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold">Interne Empfänger auswählen</h3>
          <button
            type="button"
            class="bm-btn bm-btn-ghost bm-btn-sm"
            (click)="closeAdminUserModal()"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <p class="text-sm text-base-content/60 mb-4">
          Wählen Sie die Administratoren aus, die über Wareneingänge benachrichtigt werden sollen.
        </p>

        @if (!adminUsersLoaded) {
          <div class="flex justify-center py-8">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        } @else if (adminUsers.length === 0) {
          <div class="alert alert-info">
            <i class="fa fa-info-circle"></i>
            <span>Keine Administrator-Benutzer gefunden.</span>
          </div>
        } @else {
          <div class="border border-base-300 rounded-lg overflow-hidden">
            <table class="bm-table bm-table-xs w-full">
              <thead class="bg-base-200">
                <tr>
                  <th class="w-10"></th>
                  <th>Name</th>
                  <th>E-Mail</th>
                </tr>
              </thead>
              <tbody>
                @for (user of adminUsers; track user.id) {
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    (click)="toggleAdminUser(user)"
                  >
                    <td class="text-center">
                      <input
                        type="checkbox"
                        class="bm-checkbox bm-checkbox-sm"
                        [checked]="user.selected"
                        (click)="$event.stopPropagation()"
                        (change)="toggleAdminUser(user)"
                      />
                    </td>
                    <td>{{ user.name }}</td>
                    <td class="text-base-content/60 text-sm">{{ user.email }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }

        <div class="mt-4 pt-4 border-t border-base-200 flex justify-end gap-2">
          <button
            type="button"
            class="bm-btn bm-btn-ghost"
            (click)="closeAdminUserModal()"
          >
            Abbrechen
          </button>
          <button
            type="button"
            class="bm-btn bm-btn-primary"
            (click)="applyAdminUserSelection()"
            [disabled]="!adminUsersLoaded"
          >
            Übernehmen
          </button>
        </div>
      </div>
      <div class="bm-modal-backdrop" (click)="closeAdminUserModal()"></div>
    </dialog>
  }
</hawk-record-navigation>
